import React, { useState, useRef, useEffect } from 'react';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { 
  SortIconRentCe, 
  SortIconRentDec, 
  RatingIconRent, 
  ArrowIconRent 
} from "../../AssetComponents/Images";
import { api } from '../functions/Api';

function Listings({ onSort, setSortType, filteredProperties, onSortTypeChange }) {
  const [showOrderMenu, setShowOrderMenu] = useState(false);
  const [currentSort, setCurrentSort] = useState('Sort');
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isMobile, setIsMobile] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOrderMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle mobile detection and update display name
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      
      // Update current sort display if it's not the default
      if (currentSort !== 'Sort' && currentSort !== 'Loading...') {
        const sortType = getSortTypeFromDisplay(currentSort);
        if (sortType) {
          setCurrentSort(getDisplayNameForType(sortType));
        }
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, [currentSort]);

  const getDisplayNameForType = (type) => {
    switch(type) {
      case 'lowToHigh': return isMobile ? 'Low..' : 'Low to High';
      case 'highToLow': return isMobile ? 'High..' : 'High to Low';
      case 'rating': return isMobile ? 'R..' : 'Rating';
      default: return 'Sort';
    }
  };

  const getSortTypeFromDisplay = (displayName) => {
    if (displayName.includes('Low to High') || displayName.includes('Low..')) return 'lowToHigh';
    if (displayName.includes('High to Low') || displayName.includes('High..')) return 'highToLow';
    if (displayName.includes('Rating') || displayName.includes('R..')) return 'rating';
    return null;
  };

  const handleListingTypeSort = async (type) => {
    try {
      console.log(`Sorting by: ${type}`);
      setIsLoading(true);
      setSortType(type);
      setShowOrderMenu(false);
      setCurrentSort(getDisplayNameForType(type));

      // Instead of calling separate sorting endpoint, trigger unified filtering with new sort type
      if (onSortTypeChange) {
        console.log('Triggering unified filtering with new sort type:', type);
        onSortTypeChange(type);
      } else {
        // Fallback to old behavior if onSortTypeChange not provided
        console.log('Fallback: Using separate sorting endpoint');
        const response = await api('property/getListingTypes', {
          listingType: type
        });
        
        const sortedData = response?.data || response;
        console.log('Sorted data from API:', sortedData);
        onSort(sortedData);
      }
      
    } catch (error) {
      console.error('Error handling sort type change:', error);
      toast.error('Error applying sort. Please try again.');
      
      // Reset sort state on error
      setCurrentSort('Sort');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleMenu = () => {
    if (!showOrderMenu && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const isMobile = window.innerWidth < 1024;
      
      // Calculate position relative to the button
      let left = rect.left;
      let top = rect.bottom + 8; // 8px below the button
      
      // Ensure dropdown doesn't go outside screen
      const dropdownWidth = 200; // min-width of dropdown
      const screenWidth = window.innerWidth;
      
      // Adjust left position if dropdown would go off-screen
      if (left + dropdownWidth > screenWidth) {
        left = screenWidth - dropdownWidth - 16; // 16px margin from edge
      }
      
      // Ensure minimum left position
      if (left < 16) {
        left = 16;
      }
      
      setDropdownPosition({
        top: top,
        left: left
      });
    }
    setShowOrderMenu(!showOrderMenu);
  };

  return (
    <div ref={dropdownRef} className="relative">
      <div
        ref={buttonRef}
        onClick={handleToggleMenu}
        className="py-[6px] cursor-pointer bg-white hover:bg-[#f6f6f6] rounded-[10px] px-[18px] flex justify-center gap-[12px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,257,0.8)]"
        style={{ minWidth: 'fit-content', maxWidth: '140px' }}
      >
        <div className="text-[#5b1dee] font-semibold text-base truncate">
          {isLoading ? 'Loading...' : currentSort}
        </div>
        <ArrowIconRent />
      </div>
      
      {/* Custom Dropdown Menu */}
      {showOrderMenu && createPortal(
        <div
          className="fixed bg-white rounded-[10px] shadow-[2px_2px_6px_0px_rgba(187,195,206,0.6),-2px_-2px_6px_0px_rgba(253,255,257,0.8)] min-w-[200px]"
          style={{ 
            position: 'fixed',
            zIndex: 2147483647,
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            pointerEvents: 'auto',
            isolation: 'isolate'
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <div 
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleListingTypeSort('lowToHigh');
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleListingTypeSort('lowToHigh');
            }}
            className="w-full cursor-pointer hover:bg-[#f5f5f5] rounded-t-[10px] p-[8px] gap-[8px] flex items-center"
            style={{ 
              pointerEvents: 'auto',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
          >
            <SortIconRentCe />
            <div className="text-[#666666]">Price: Low to High</div>
          </div>
          <div 
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleListingTypeSort('highToLow');
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleListingTypeSort('highToLow');
            }}
            className="w-full cursor-pointer hover:bg-[#f5f5f5] p-[8px] gap-[8px] flex items-center"
            style={{ 
              pointerEvents: 'auto',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
          >
            <SortIconRentDec />
            <div className="text-[#666666]">Price: High to Low</div>
          </div>
          <div 
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleListingTypeSort('rating');
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleListingTypeSort('rating');
            }}
            className="w-full cursor-pointer hover:bg-[#f5f5f5] rounded-b-[10px] p-[8px] gap-[8px] flex items-center"
            style={{ 
              pointerEvents: 'auto',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
          >
            <RatingIconRent />
            <div className="text-[#666666]">Rating</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default Listings;